import * as mobx from 'mobx';
import { ChangeEvent } from 'react';
import * as IoC from 'typescript-ioc';
import { IntlShape } from 'react-intl';
import { IEventDetailsDTO, IEventsDAO, IWebsiteDetailsDTO } from '../../../../../api/events.dao';
import { IWebsiteDAO } from '../../../../../api/website.dao';
import { MEDIUM_PLAIN_TEXT_LENGTH, RADIX_PARAM } from '../../../../../constants/common.constants';
import { LINK_REG_EXP } from '../../../../../constants/regex.constants';
import { getWebsiteFieldValidationError } from '../../../../../infrastructure/utils/getWebsiteFieldValidationError';
import { ISubWindowFacade, ISubWindowsService, SubWindowStatus } from '../../../../../infrastructure/sub-windows';
import { IErrorParsing } from '../../../../../infrastructure/error-parsing';
import { CustomPageConfigurationType } from '../../../../../api/custom-pages.dao';
import {
    FIELD_IS_REQUIRED_ERROR_MESSAGE,
    MAX_LENGTH_ERROR_MESSAGE,
    MAX_URL_LENGTH_ERROR_MESSAGE,
    NOT_ALLOWED_CHARACTERS_ERROR_MESSAGE,
    SHORT_PLAIN_TEXT_TOO_LONG_ERROR_MESSAGE,
} from '../../../../../constants/validation-errors';
import { FooterLink } from './website-footer-links/website-footer-link.presentation';
import { IPreventJoinBeforeProps, IPreventJoinBeforeValues } from './prevent-join-before';
import CustomFieldVM from './registration-form/custom-field/custom-field.viewmodel';
import { EmailsTemplateFormVM } from './emails-template-form/emails-template-form.viewmodel';
import { SignInForm } from './sign-in-form/sign-in-form.container';
import { GeneralRegistrationForm } from './registration-form/general-registration-form/general-registration-form.container';
import { ConfPassNavForm } from './conf-passport-nav-form/conf-passport-nav-form.container';
import { PassportSettingsForm } from './passport-settings-form/passport-settings-form.container';
import { FormRegistrationForm } from './registration-form/custom-fields-form/form-registration-form.container';
import { CustomPagesConfigurationVM } from './passport-pages/custom-pages/custom-pages.viewmodel';
import { DefaultPagesConfigurationVM } from './passport-pages/default-pages/default-pages.viewmodel';
import { orderPages } from './helpers';

import { CustomCssForm } from './custom-css-form/custom-css-form';
import { CustomCssFormVM } from './custom-css-form/custom-css-form.viewmodel';
import { UrlRedirectionForm } from './url-redirection-form/url-redirection-form.container';
import { CONFIGURE_URL_REDIRECTION_TEXT } from './url-redirection-form/url-redirection-form.texts';
import { IPassportUrlRedirectionDAO, IPassportUrlRedirectionDTO } from '../../../../../api/passport-url-redirection.dao';
import { ScheduledEmailsForm } from './scheduled-emails-form/scheduled-emails-form';
import { EmailTemplatesForm } from './emails-template-form/email-templates-form/email-templates-form';
import { isValidAsShortPlainText, isValidAsSpecialCharacters } from '../../../../../infrastructure/utils/form-validation.utils';
import { IAlertsService } from '../../../../../infrastructure/alerts';
import { hasPermission } from '../../../../../infrastructure/utils/has-permissions';
import { Permission } from '../../../../../api/roles.dao';
import {
    CONFIGURE_CUSTOM_CSS_TEXT,
    CONFIGURE_NAVIGATION_TEXT,
    CONFIGURE_PASSPORT_SETTINGS_TEXT,
    CONFIGURE_RESPONSIVE_EMAILS_TEXT,
    CONFIGURE_SCHEDULED_EMAILS_TEXT,
    CONFIGURE_SIGN_IN_TEXT,
    CONFIGURE_TITLE_AND_DESCRIPTION_TEXT
} from './website-form.texts';
import { ScheduledEmailsFormManagement } from './scheduled-emails-form/scheduled-emails-form-management.viewmodel';
import { EditPassportTitleForm } from './edit-passport-title-form/edit-passport-title-form.container';
import { alertServerError, handleApiException } from '../../../../global/modal/alerts-host/alert-templates';

const defaultValues = {
    emailDisplayName: 'Downloads and Links'
};
class WebsiteFormBodyVM {
    @mobx.observable
    private isChecked: boolean;
    @mobx.observable
    public isResetCustomPages: boolean;
    @mobx.observable
    public validationError: string[];
    @mobx.observable
    private serverErrorMessage: string;
    @mobx.observable
    private subdomain: string;
    @mobx.observable
    private accountSubdomain: string;
    @mobx.observable
    private customCss: string;
    @mobx.observable
    private footerLinks: FooterLink[];
    @mobx.observable
    private footerLinkLabel: string;
    @mobx.observable
    private customFieldLabel: string;
    @mobx.observable
    private customFieldCheckbox: boolean;
    @mobx.observable
    private footerLinkUrl: string;
    @mobx.observable
    private showFieldForNewFooterLink: boolean;
    @mobx.observable
    private setFooterLinkUrlValidationError: string;
    @mobx.observable
    private setFooterLinkLabelValidationError: string;
    private reservedSubdomainArray: string[];
    private readonly organizationName: string;
    @mobx.observable
    public registrationAsLandingPage: boolean;
    @mobx.observable
    public preventJoinBefore: number;
    @mobx.observable
    public preventJoinEnabled: boolean;
    @mobx.observable
    public registrationEnabled: boolean;
    @mobx.observable
    private registrationFormPopupMap: ISubWindowFacade;
    @mobx.observable
    private formRegistrationFormPopupMap: ISubWindowFacade;
    @mobx.observable
    private passportNavFormPopupMap: ISubWindowFacade;
    @mobx.observable
    private signInFormPopupMap: ISubWindowFacade;
    @mobx.observable
    private passportSettingsFormPopupMap: ISubWindowFacade;
    @mobx.observable
    private editPassportTitleFormPopupMap: ISubWindowFacade;
    @mobx.observable
    private showEmailsTemplateFormPopupMap: ISubWindowFacade;
    @mobx.observable
    private showScheduledEmailsFormPopupMap: ISubWindowFacade;
    @mobx.observable
    private showCustomCssFormPopupMap: ISubWindowFacade;
    @mobx.observable
    private urlRedirectionFormPopupMap: ISubWindowFacade;
    @mobx.observable
    public urlRedirectionEnabled = false;
    @mobx.observable
    public redirectUrl = '';
    @mobx.observable
    private timeZone: string;
    private readonly accountId: number;
    private readonly eventId: number;
    @mobx.observable
    public customFieldView: CustomFieldVM;
    public setSubdomainInPassportTab: (value: string) => void;

    public customPagesVM: CustomPagesConfigurationVM;
    public defaultPagesVM: DefaultPagesConfigurationVM;
    private emailTemplateFormVM: EmailsTemplateFormVM;
    @mobx.observable
    public pages: CustomPageConfigurationType[] = [];
    @mobx.observable
    public emailDisplayName: string;
    @mobx.observable
    public disableEmailDisplayName: boolean;
    @mobx.observable
    public meetingLinksLabel: string;
    @mobx.observable
    public disableMeetingLinksLabel: boolean;
    @mobx.observable
    public registrationApprovalEnabled: boolean;
    @mobx.observable
    public requestAccessCodeRegistrationApprovalEnabled: boolean;
    @mobx.observable
    public urlParameterRegistrationEnabled: boolean;
    constructor(
        props: Partial<IWebsiteDetailsDTO>,
        private readonly intl: IntlShape,
        private readonly event: IEventDetailsDTO,
        private readonly websiteDAO: IWebsiteDAO = IoC.Container.get(IWebsiteDAO),
        private readonly eventsDAO: IEventsDAO = IoC.Container.get(IEventsDAO),
        private readonly errorParsingService: IErrorParsing = IoC.Container.get(IErrorParsing),
        @IoC.Inject private readonly alertsService: IAlertsService = IoC.Container.get(IAlertsService),
        @IoC.Inject private readonly subWindowService: ISubWindowsService = IoC.Container.get(ISubWindowsService),
        @IoC.Inject private readonly urlRedirectionDAO: IPassportUrlRedirectionDAO = IoC.Container.get(IPassportUrlRedirectionDAO)
    ) {
        this.footerLinks = props.footerLinks || [];
        this.subdomain = props.subdomain;
        this.accountSubdomain = props.accountSubdomain;
        this.organizationName = props.organizationName;
        this.accountId = props.accountId;
        this.eventId = props.eventId;
        this.customCss = props.customCss || '';
        this.showFieldForNewFooterLink = false;
        this.footerLinkUrl = '';
        this.preventJoinBefore = props.preventJoinBefore;
        this.preventJoinEnabled = !!props.preventJoinEnabled;
        this.registrationEnabled = !!props.registrationEnabled;
        void this.fetchReservedSubDomainArray();
        this.serverErrorMessage = '';
        this.registrationAsLandingPage = !!props.registrationAsLandingPage;
        this.isResetCustomPages = false;
        this.timeZone = props.timeZone;
        this.emailDisplayName = props.emailDisplayName ?? '';
        this.disableEmailDisplayName = true;
        this.meetingLinksLabel = props.meetingLinksLabel ?? defaultValues.emailDisplayName;
        this.disableMeetingLinksLabel = true;
        this.registrationApprovalEnabled = !!props.registrationApprovalEnabled;
        this.requestAccessCodeRegistrationApprovalEnabled = !!props.requestAccessCodeRegistrationApprovalEnabled;
        this.urlParameterRegistrationEnabled = !!props.urlParameterRegistrationEnabled;

        this.customPagesVM = new CustomPagesConfigurationVM(props.accountId, props.eventId, this.loadPages);
        this.defaultPagesVM = new DefaultPagesConfigurationVM(
            props.accountId,
            props.eventId,
            this.intl,
            this.loadPages
        );
        this.emailTemplateFormVM = new EmailsTemplateFormVM({
            accountId: this.accountId,
            eventId: this.eventId,
            intl: this.intl,
        });
        void this.customPagesVM.initialize();
        void this.customPagesVM.init();
        void this.loadPages();
        void this.loadUrlRedirection();
    }

    public loadPages = async (pagesList?: CustomPageConfigurationType[]) => {
        let pages = pagesList;
        if (!pagesList && hasPermission(Permission.PASSPORT_TAB)) {
            ({ pages: pages } = await this.websiteDAO.getAllPagesConfiguration({
                accountId: this.accountId,
                eventId: this.eventId,
            }));
        }
        mobx.runInAction(() => {
            this.pages = orderPages(pages).pagesList;
        });
    };

    public loadUrlRedirection = async (): Promise<void> => {
        if (!hasPermission(Permission.PASSPORT_TAB)) {
            return;
        }
        try {
            const data = await this.urlRedirectionDAO.getPassportUrlRedirection({
                accountId: this.accountId,
                eventId: this.eventId,
            });
            this.setUrlRedirection(data);
        } catch (e) {
            handleApiException(e);
        }
    };

    @mobx.action
    public setUrlRedirection = (data: IPassportUrlRedirectionDTO): void => {
        this.urlRedirectionEnabled = !!data?.urlRedirectionEnabled;
        this.redirectUrl = data?.redirectUrl ?? '';
    };

    private async fetchReservedSubDomainArray() {
        this.reservedSubdomainArray = await this.websiteDAO.getReservedSubDomainArray();
    }

    @mobx.action
    public toggleAttendeeRegistrationWebsiteForm = (registrationEnabled: boolean): void => {
        this.registrationEnabled = registrationEnabled;
    };

    @mobx.action
    public toggleRegistrationAsLandingWebsiteForm = (registrationAsLandingPage: boolean): void => {
        this.registrationAsLandingPage = registrationAsLandingPage;
    };

    public getPreventJoinBeforeValues = (): IPreventJoinBeforeValues => {
        return {
            preventJoinBefore: this.preventJoinBefore,
            preventJoinEnabled: this.preventJoinEnabled,
        };
    };

    public composePreventJoinBeforeProps = (): IPreventJoinBeforeProps => {
        return {
            ...this.getPreventJoinBeforeValues(),
            togglePreventJoinBefore: this.togglePreventJoinBefore,
            onPreventJoinBeforeChange: this.onPreventJoinBeforeChange,
            getPreventJoinBeforeValidationError: this.getPreventJoinBeforeValidationError,
        };
    };

    @mobx.action
    public togglePreventJoinBefore = (e: ChangeEvent<HTMLInputElement>): void => {
        this.preventJoinEnabled = e.target.checked;
        this.preventJoinBefore = 10;
    };
    @mobx.action
    public onPreventJoinBeforeChange = (e: ChangeEvent<HTMLInputElement>): void => {
        this.preventJoinBefore = e.target.value && parseInt(e.target.value, RADIX_PARAM);
    };

    public getPreventJoinBeforeValidationError = (): string | null => {
        if (!this.preventJoinEnabled) return null;
        if (!this.preventJoinBefore) {
            return 'Please enter the number of minutes prior to the meeting to enable the Join button';
        }
        if (this.preventJoinBefore < 1 || this.preventJoinBefore > 99) {
            return 'The value must be between 1 and 99';
        }
    };

    public setIsResetCustomPages = (): void => {
        this.isResetCustomPages = !this.isResetCustomPages;
    };

    public getEventParams = (): { accountId: number; eventId: number } => {
        return {
            accountId: this.accountId,
            eventId: this.eventId,
        };
    };

    @mobx.action
    public validateWebsite = (): boolean => {
        if (this.footerLinkUrl) this.validateFooterLinkLabel();

        this.validateFooterLinkUrl();
        this.validateFooterLinks();

        return [
            this.setFooterLinkLabelValidationError,
            this.setFooterLinkUrlValidationError,
            getWebsiteFieldValidationError(this.getSubdomain()),
            this.getPreventJoinBeforeValidationError(),
        ].some(Boolean);
    };

    @mobx.action
    public setSubdomain = (value: string) => {
        this.subdomain = value;
    };

    public getFooterLinks(): FooterLink[] {
        return this.footerLinks;
    }

    @mobx.action
    public removeFooterLinkByValue = (value: FooterLink) => {
        this.footerLinks = this.footerLinks.filter(item => item !== value);
    };

    @mobx.action
    public addNewFooterLink = () => {
        this.validateFooterLinkUrl();
        if (this.footerLinkUrl || this.footerLinkLabel) {
            this.validateFooterLinkLabel();
        }

        if (this.footerLinks.length >= 49) return;

        if (this.setFooterLinkLabelValidationError || this.setFooterLinkUrlValidationError) return;

        this.changeNewFooterLinkFieldStatus();

        if (!this.setFooterLinkLabelValidationError && !this.setFooterLinkUrlValidationError && this.footerLinkUrl) {
            const item = {
                order: this.footerLinks.length + 1,
                label: this.footerLinkLabel.trim(),
                url: this.footerLinkUrl.trim(),
            };

            this.footerLinks.push(item);
            this.cleanNewFooterLinkItem();
        }
    };

    @mobx.action
    public showGeneralRegistrationFormPopup = () => {
        if (!this.registrationFormPopupMap || this.registrationFormPopupMap?.getStatus() === SubWindowStatus.Closed) {
            this.registrationFormPopupMap = this.subWindowService.showSubWindow({
                title: 'Configure Registration',
                componentToHost: GeneralRegistrationForm,
                componentToHostProps: {
                    accountId: this.accountId,
                    eventId: this.eventId,
                    subdomain: this.subdomain,
                    registrationEnabled: this.registrationEnabled,
                    registrationApprovalEnabled: this.registrationApprovalEnabled,
                    urlParameterRegistrationEnabled: this.urlParameterRegistrationEnabled,
                    toggleAttendeeRegistrationWebsiteForm: this.toggleAttendeeRegistrationWebsiteForm,
                    showFormRegistrationFormPopup: this.showFormRegistrationFormPopup,
                    registrationAsLandingPage: this.registrationAsLandingPage,
                    toggleRegistrationAsLandingWebsiteForm: this.toggleRegistrationAsLandingWebsiteForm,
                    closeConfigureRegistrationForm: this.closeConfigureRegistrationForm,
                },
            });
        } else {
            this.registrationFormPopupMap?.maximize();
        }
    };

    private closeConfigureRegistrationForm = (): void => {
        this.registrationFormPopupMap?.close();
    };

    public isGeneralRegistrationOpened = () => {
        return this.registrationFormPopupMap && this.registrationFormPopupMap.getStatus() !== SubWindowStatus.Closed;
    };

    public showFormRegistrationFormPopup = () => {
        if (
            !this.formRegistrationFormPopupMap ||
            this.formRegistrationFormPopupMap?.getStatus() === SubWindowStatus.Closed
        ) {
            this.formRegistrationFormPopupMap = this.subWindowService.showSubWindow({
                title: 'Configure Registration Form',
                componentToHost: FormRegistrationForm,
                componentToHostProps: {
                    accountId: this.accountId,
                    eventId: this.eventId,
                    subdomain: this.subdomain,
                    closeConfigureRegistrationForm: this.closeConfigureRegistrationForm,
                },
            });
        }
    };

    public getFooterLinkUrlValidationError = (): string => {
        return this.setFooterLinkUrlValidationError;
    };

    public getFooterLinkLabelValidationError = (): string => {
        return this.setFooterLinkLabelValidationError;
    };

    @mobx.action
    public setFooterLinkUrl = (val: string) => {
        this.cleanFooterLinkValidationErrors();
        this.footerLinkUrl = val;
    };

    @mobx.action
    public setFooterLinkLabel = (val: string) => {
        this.cleanFooterLinkValidationErrors();
        this.footerLinkLabel = val;
    };

    @mobx.action
    public validateFooterLinkUrl = (): void => {
        if (!this.footerLinkUrl && !this.footerLinkLabel) return;

        if (this.footerLinkUrl.length > MEDIUM_PLAIN_TEXT_LENGTH) {
            this.setFooterLinkUrlValidationError = MAX_URL_LENGTH_ERROR_MESSAGE;
        } else if (!this.footerLinkUrl.match(LINK_REG_EXP)) {
            this.setFooterLinkUrlValidationError = 'Please enter a valid URL beginning with http:// or https://';
        } else {
            this.setFooterLinkUrlValidationError = '';
        }
    };

    @mobx.action
    public validateFooterLinkLabel = (): void => {
        if (!this.footerLinkLabel || this.footerLinkLabel.length > 255) {
            this.setFooterLinkLabelValidationError = MAX_LENGTH_ERROR_MESSAGE;
        } else {
            this.setFooterLinkLabelValidationError = '';
        }
    };

    @mobx.action
    public changeNewFooterLinkFieldStatus = (): boolean => {
        return (this.showFieldForNewFooterLink = !this.showFieldForNewFooterLink);
    };

    @mobx.action
    private validateFooterLinks = () => {
        if (
            this.footerLinkUrl &&
            this.footerLinkLabel &&
            !this.setFooterLinkUrlValidationError &&
            !this.setFooterLinkLabelValidationError
        ) {
            const item = {
                order: this.footerLinks.length + 1,
                label: this.footerLinkLabel.trim(),
                url: this.footerLinkUrl.trim(),
            };

            this.footerLinks.push(item);
            this.cleanNewFooterLinkItem();
            this.showFieldForNewFooterLink = false;
        }
    };

    public getFooterLinkFieldStatus = (): boolean => {
        return this.showFieldForNewFooterLink;
    };

    public getFooterLink = (): FooterLink => {
        return {
            order: this.footerLinks.length,
            label: this.footerLinkLabel,
            url: this.footerLinkUrl,
        };
    };

    public getSubdomain(): string {
        return this.subdomain;
    }

    public getAccountSubdomain(): string {
        return this.accountSubdomain;
    }

    public getServerErrorMessage(): string {
        return this.serverErrorMessage;
    }

    public getCustomCss(): string {
        return this.customCss;
    }

    public getRegistrationAsLandingPage(): boolean {
        return this.registrationAsLandingPage;
    }

    @mobx.action
    private cleanNewFooterLinkItem = (): void => {
        this.footerLinkLabel = '';
        this.footerLinkUrl = '';
    };

    @mobx.action
    private cleanFooterLinkValidationErrors = () => {
        this.setFooterLinkUrlValidationError = '';
        this.setFooterLinkLabelValidationError = '';
    };

    public showEmailsTemplateForm = () => {
        if (
            !this.showEmailsTemplateFormPopupMap ||
            this.showEmailsTemplateFormPopupMap?.getStatus() === SubWindowStatus.Closed
        ) {
            this.showEmailsTemplateFormPopupMap = this.subWindowService.showSubWindow({
                componentToHost: EmailTemplatesForm,
                componentToHostProps: {
                    vm: this.emailTemplateFormVM,
                },
                className: 'email-templates',
                title: this.intl.formatMessage(CONFIGURE_RESPONSIVE_EMAILS_TEXT),
            });
        }
    };

    public showScheduledEmails = () => {
        if (
            !this.showScheduledEmailsFormPopupMap ||
            this.showScheduledEmailsFormPopupMap?.getStatus() === SubWindowStatus.Closed
        ) {
            const scheduledEmailsFormVM = new ScheduledEmailsFormManagement(
                this.accountId,
                this.event,
                {}
            );

            this.showScheduledEmailsFormPopupMap = this.subWindowService.showSubWindow({
                componentToHost: ScheduledEmailsForm,
                componentToHostProps: {
                    vm: scheduledEmailsFormVM,
                },
                className: 'scheduled-emails',
                title: this.intl.formatMessage(CONFIGURE_SCHEDULED_EMAILS_TEXT),
            });
        }
    };

    public showCustomCssForm = (intl: IntlShape) => {
        if (!this.showCustomCssFormPopupMap || this.showCustomCssFormPopupMap?.getStatus() === SubWindowStatus.Closed) {
            const customCssFormVM = new CustomCssFormVM({
                accountId: this.accountId,
                eventId: this.eventId,
                customCss: this.customCss,
                updateCustomCss: this.updateCustomCss
            });

            this.showCustomCssFormPopupMap = this.subWindowService.showSubWindow({
                componentToHost: CustomCssForm,
                componentToHostProps: {
                    vm: customCssFormVM
                },
                withExpand: true,
                isExpandedByDefault: true,
                className: 'custom-css',
                title: intl.formatMessage(CONFIGURE_CUSTOM_CSS_TEXT)
            });
        }
    };

    @mobx.action
    public updateCustomCss = (customCss: string) => {
        this.customCss = customCss;
    };

    public showConfPassNavPopup = () => {
        if (!this.passportNavFormPopupMap || this.passportNavFormPopupMap?.getStatus() === SubWindowStatus.Closed) {
            this.passportNavFormPopupMap = this.subWindowService.showSubWindow({
                title: this.intl.formatMessage(CONFIGURE_NAVIGATION_TEXT),
                componentToHost: ConfPassNavForm,
                componentToHostProps: {
                    accountId: this.accountId,
                    eventId: this.eventId,
                    subdomain: this.subdomain,
                    setIsResetCustomPages: this.setIsResetCustomPages,
                    onSuccess: pages => {
                        void this.loadPages(pages);
                        void this.customPagesVM.initCustomPages();
                    },
                },
            });
        }
    };

    @mobx.action
    public showSignInPopup = async (): Promise<void> => {
        if (!this.signInFormPopupMap || this.signInFormPopupMap?.getStatus() === SubWindowStatus.Closed) {
            try {
                const eventDetails = await this.eventsDAO.getEventDetails({
                    accountId: this.accountId,
                    eventId: this.eventId,
                });
                mobx.runInAction(() => {
                    this.requestAccessCodeRegistrationApprovalEnabled =
                        !!eventDetails.requestAccessCodeRegistrationApprovalEnabled;
                });
            } catch (e) {
                alertServerError('Error', this.errorParsingService.extractMessage(e));
            }

            mobx.runInAction(() => {
                this.signInFormPopupMap = this.subWindowService.showSubWindow({
                    title: this.intl.formatMessage(CONFIGURE_SIGN_IN_TEXT),
                    componentToHost: SignInForm,
                    componentToHostProps: {
                        accountId: this.accountId,
                        eventId: this.eventId,
                        subdomain: this.subdomain,
                        requestAccessCodeRegistrationApprovalEnabled:
                            this.requestAccessCodeRegistrationApprovalEnabled,
                    },
                });
            });
        } else {
            this.signInFormPopupMap?.maximize();
        }
    };

    public isSignInPopupOpened = (): boolean => {
        return this.signInFormPopupMap && this.signInFormPopupMap.getStatus() !== SubWindowStatus.Closed;
    };

    public isPassportSettingsPopupOpened = (): boolean => {
        return this.passportSettingsFormPopupMap && this.passportSettingsFormPopupMap.getStatus() !== SubWindowStatus.Closed;
    };

    public isEditPassportTitlePopupOpened = (): boolean => {
        return this.editPassportTitleFormPopupMap && this.editPassportTitleFormPopupMap.getStatus() !== SubWindowStatus.Closed;
    };

    @mobx.action
    public showPassportSettingsPopup = (): void => {
        if (!this.isPassportSettingsPopupOpened()) {
            this.passportSettingsFormPopupMap = this.subWindowService.showSubWindow({
                withExpand: true,
                title: this.intl.formatMessage(CONFIGURE_PASSPORT_SETTINGS_TEXT),
                componentToHost: PassportSettingsForm,
                componentToHostProps: {
                    accountId: this.accountId,
                    eventId: this.eventId,
                    setSubdomainInPassportTab: this.setSubdomainInPassportTab,
                    accountSubdomain: this.accountSubdomain,
                },
            });
        }
    };

    public isUrlRedirectionPopupOpened = (): boolean => {
        return this.urlRedirectionFormPopupMap && this.urlRedirectionFormPopupMap.getStatus() !== SubWindowStatus.Closed;
    };

    @mobx.action
    public showUrlRedirectionPopup = (): void => {
        if (!this.isUrlRedirectionPopupOpened()) {
            this.urlRedirectionFormPopupMap = this.subWindowService.showSubWindow({
                title: this.intl.formatMessage(CONFIGURE_URL_REDIRECTION_TEXT),
                componentToHost: UrlRedirectionForm,
                componentToHostProps: {
                    accountId: this.accountId,
                    eventId: this.eventId,
                    intl: this.intl,
                    urlRedirectionEnabled: this.urlRedirectionEnabled,
                    redirectUrl: this.redirectUrl,
                    onSaved: this.setUrlRedirection,
                },
            });
        } else {
            this.urlRedirectionFormPopupMap?.maximize();
        }
    };

    @mobx.action
    public showEditPassportTitlePopup = (): void => {
        if (!this.isEditPassportTitlePopupOpened()) {
            this.editPassportTitleFormPopupMap = this.subWindowService.showSubWindow({
                withExpand: true,
                title: this.intl.formatMessage(CONFIGURE_TITLE_AND_DESCRIPTION_TEXT),
                componentToHost: EditPassportTitleForm,
                componentToHostProps: {
                    accountId: this.accountId,
                    eventId: this.eventId,
                    intl: this.intl,
                },
            });
        }
    };

    @mobx.action
    public setEmailDisplayName = (emailDisplayName: string): void => {
        this.emailDisplayName = emailDisplayName;
    };

    @mobx.action
    public setDisableEmailDisplayName = (value: boolean): void => {
        this.disableEmailDisplayName = value;
    };

    public getEmailDisplayNameError = (): string | null => {
        if (isValidAsSpecialCharacters(this.emailDisplayName)) {
            return NOT_ALLOWED_CHARACTERS_ERROR_MESSAGE(
                this.intl,
                '< > ; : " [ ] ( )'
            );
        }
        if (!isValidAsShortPlainText(this.emailDisplayName)) {
            return SHORT_PLAIN_TEXT_TOO_LONG_ERROR_MESSAGE(this.intl);
        }
        return null;
    };

    @mobx.action
    public saveEmailAddressDisplayName = async () => {
        try {
            await this.websiteDAO.updateEmailAddressDisplayName({
                accountId: this.accountId,
                eventId: this.eventId,
                emailDisplayName: this.emailDisplayName
            });
        } catch (error) {
            this.alertsService.alert({
                message: this.errorParsingService.extractMessage(error)
            });
            mobx.runInAction(() => {
                this.emailDisplayName = '';
            });
        } finally {
            mobx.runInAction(() => {
                this.disableEmailDisplayName = true;
            });
        }
    };

    @mobx.action
    public setMeetingLinksLabel = (meetingLinksSectionName: string): void => {
        this.meetingLinksLabel = meetingLinksSectionName;
    };

    @mobx.action
    public setDisableMeetingLinksLabel = (value: boolean): void => {
        this.disableMeetingLinksLabel = value;
    };

    public getMeetingLinksLabelError = (): string | null => {
        if (!this.meetingLinksLabel) {
            return FIELD_IS_REQUIRED_ERROR_MESSAGE('Label for meeting links', this.intl);
        }
        if (!isValidAsShortPlainText(this.meetingLinksLabel)) {
            return SHORT_PLAIN_TEXT_TOO_LONG_ERROR_MESSAGE(this.intl);
        }
        return null;
    };

    @mobx.action
    public saveMeetingLinksLabel = async (): Promise<void> => {
        try {
            await this.websiteDAO.updateMeetingLinksLabel({
                accountId: this.accountId,
                eventId: this.eventId,
                meetingLinksLabel: this.meetingLinksLabel
            });
        } catch (error) {
            this.alertsService.alert({
                message: this.errorParsingService.extractMessage(error)
            });
            mobx.runInAction(() => {
                this.meetingLinksLabel = '';
            });
        } finally {
            mobx.runInAction(() => {
                this.disableMeetingLinksLabel = true;
            });
        }
    };


    public closeSubWindows = (): void => {
        this.passportSettingsFormPopupMap?.close();
        this.signInFormPopupMap?.close();
        this.passportNavFormPopupMap?.close();
        this.registrationFormPopupMap?.close();
        this.showCustomCssFormPopupMap?.close();
        this.urlRedirectionFormPopupMap?.close();
        this.formRegistrationFormPopupMap?.close();
        this.showEmailsTemplateFormPopupMap?.close();
        this.showScheduledEmailsFormPopupMap?.close();
        this.defaultPagesVM.closeAllSubWindows();
    };
}

export { WebsiteFormBodyVM };
